import { BrowserRouter as Router, Route, useParams, useLocation, Navigate, Outlet } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import i18n, { SUPPORTED_LANGS, type Lang } from './i18n'
import PageTransition from './components/PageTransition'
import { useAnalytics } from './hooks/useAnalytics'
import ErrorBoundary from './components/ErrorBoundary'
// Sayfalar lazy yüklenir — ilk paket boyutu düşer, her rota kendi chunk'ında.
// İmport fonksiyonları tek yerde toplanır ki ilk yüklemeden sonra idle'da
// hepsi önceden cache'lenebilsin (rota geçişlerinde lazy gap / yeşil flash olmasın).
/**
 * Yeni deploy sonrası eski index.html elinde kalan sekmelerde, hash'i değişmiş
 * chunk'ın import()'u reddedilir; Suspense hiç çözülmez ve preloader ekranda
 * sonsuza kadar kalır (kullanıcı ancak F5 ile kurtulur). Burada bir kez retry
 * ediyor, yine olmazsa oturumda TEK kez tam reload yapıyoruz — böylece geçiş
 * kendiliğinden tamamlanır.
 */
const RELOAD_FLAG = 'hnw-chunk-reloaded'
function lazyPage<T extends { default: React.ComponentType<unknown> }>(factory: () => Promise<T>) {
  return lazy(() =>
    factory().catch(() =>
      factory().catch((err) => {
        if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1')
          window.location.reload()
        }
        throw err
      })
    )
  )
}

const load = {
  Home: () => import('./pages/Home'),
  SuitsAndVillas: () => import('./pages/SuitsAndVillas'),
  Suits: () => import('./pages/Suits'),
  Villas: () => import('./pages/Villas'),
  Contact: () => import('./pages/Contact'),
  AboutHotel: () => import('./pages/AboutHotel'),
  Facility: () => import('./pages/Facility'),
  LifeAtHiraNova: () => import('./pages/LifeAtHiraNova'),
  Restaurant: () => import('./pages/Restaurant'),
  Location: () => import('./pages/Location'),
  Gallery: () => import('./pages/Gallery'),
  GenelPlan: () => import('./pages/GenelPlan'),
  VillaDetail: () => import('./pages/VillaDetail'),
  AmenityDetail: () => import('./pages/AmenityDetail'),
  Destinations: () => import('./pages/Destinations'),
  Membership: () => import('./pages/Membership'),
  SosyalKulup: () => import('./pages/SosyalKulup'),
  Activities: () => import('./pages/Activities'),
  Representatives: () => import('./pages/Representatives'),
  Packages: () => import('./pages/Packages'),
  Academy: () => import('./pages/Academy'),
}
const Home = lazyPage(load.Home)
const SuitsAndVillas = lazyPage(load.SuitsAndVillas)
const Suits = lazyPage(load.Suits)
const Villas = lazyPage(load.Villas)
const Contact = lazyPage(load.Contact)

const Facility = lazyPage(load.Facility)
const LifeAtHiraNova = lazyPage(load.LifeAtHiraNova)
const Restaurant = lazyPage(load.Restaurant)
const Location = lazyPage(load.Location)
const Gallery = lazyPage(load.Gallery)
const GenelPlan = lazyPage(load.GenelPlan)
const VillaDetail = lazyPage(load.VillaDetail)
const AmenityDetail = lazyPage(load.AmenityDetail)
const Destinations = lazyPage(load.Destinations)
const Membership = lazyPage(load.Membership)
const SosyalKulup = lazyPage(load.SosyalKulup)
const Activities = lazyPage(load.Activities)
const Representatives = lazyPage(load.Representatives)
const Packages = lazyPage(load.Packages)
const Academy = lazyPage(load.Academy)
import GlobalMobileBar from './components/GlobalMobileBar'
import SiteAudioBar from './components/SiteAudioBar'
import ChatWidget from './components/ChatWidget'
import './App.css'

/**
 * URL prefix'inden dil tespit edip i18next'i o dile ayarlar.
 * /en/genel-plan → 'en', /ar/villa/suite-1 → 'ar', vs.
 * Geçersiz dil prefix'i ise default'a yönlendirir.
 */
function LangPrefixGuard({ children }: { children: React.ReactNode }) {
  const { lang } = useParams<{ lang: string }>()

  useEffect(() => {
    if (lang && (SUPPORTED_LANGS as string[]).includes(lang) && lang !== 'tr') {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang as Lang)
      }
    }
  }, [lang])

  if (!lang || !(SUPPORTED_LANGS as string[]).includes(lang)) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

/**
 * Default (TR) için route'larda dili 'tr'ye ayarlar.
 * Kullanıcı /en/... → /... gibi geri dönerse TR'ye geçer.
 */
function DefaultLangGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  useEffect(() => {
    // URL prefix yoksa TR (varsayılan)
    const hasPrefix = /^\/(en|de|ru|ar|fr|zh)(\/|$)/.test(location.pathname)
    if (!hasPrefix && i18n.language !== 'tr') {
      i18n.changeLanguage('tr')
    }
  }, [location.pathname])
  return <>{children}</>
}

/**
 * Ziyaretçi analitiği — Router içinde olmalı (rota değişimini dinliyor).
 * Görsel çıktısı yok; yalnızca pageview/click olayı gönderir.
 */
function Analytics() {
    useAnalytics()
    return null
}

function App() {
    // İlk yüklemeden sonra boşta tüm rota chunk'larını önceden cache'le —
    // sonraki geçişlerde lazy gap (yeşil flash) olmaz, sayfa anında gelir.
    useEffect(() => {
        const prefetch = () => { for (const fn of Object.values(load)) fn().catch(() => {}) }
        const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback
        if (ric) { ric(prefetch); return }
        const id = window.setTimeout(prefetch, 1500)
        return () => window.clearTimeout(id)
    }, [])

    // Vite'ın modulepreload hatası da aynı stale-chunk durumudur → tek seferlik reload
    useEffect(() => {
        const onPreloadError = (e: Event) => {
            e.preventDefault()
            if (!sessionStorage.getItem(RELOAD_FLAG)) {
                sessionStorage.setItem(RELOAD_FLAG, '1')
                window.location.reload()
            }
        }
        window.addEventListener('vite:preloadError', onPreloadError)
        return () => window.removeEventListener('vite:preloadError', onPreloadError)
    }, [])

    return (
        <Router>
            <Analytics />
            <ErrorBoundary>
            <Suspense fallback={<div className="page-loading" />}>
            <PageTransition>
                {/* ────────────────── DEFAULT (TR) ────────────────── */}
                <Route element={<DefaultLangGuard><GuardOutlet /></DefaultLangGuard>}>
                    <Route path="/" element={<Home />} />
                    <Route path="/suits-and-villas" element={<SuitsAndVillas />} />
                    <Route path="/suite-villas" element={<Suits />} />
                    <Route path="/garden-villas" element={<Villas />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/about" element={<Navigate to="/life-at-hiranova" replace />} />
                    <Route path="/facility" element={<Facility />} />
                    <Route path="/life-at-hiranova" element={<LifeAtHiraNova />} />
                    <Route path="/restaurant" element={<Restaurant />} />
                    <Route path="/location" element={<Location />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/genel-plan" element={<GenelPlan />} />
                    <Route path="/villa/:villaId" element={<VillaDetail />} />
                    <Route path="/tesis/:amenityId" element={<AmenityDetail />} />
                    <Route path="/felsefe" element={<Navigate to="/life-at-hiranova" replace />} />
                    <Route path="/destinasyonlar" element={<Destinations />} />
                    <Route path="/uyelik-basvurusu" element={<Membership />} />
                    <Route path="/sosyal-kulup" element={<SosyalKulup />} />
                    <Route path="/aktiviteler" element={<Activities />} />
                    <Route path="/cocuk-kulubu" element={<Navigate to="/cocuk-akademisi" replace />} />
                    <Route path="/cocuk-akademisi" element={<Academy />} />
                    <Route path="/temsilcilerimiz" element={<Representatives />} />
                    <Route path="/uyelik-paketleri" element={<Packages />} />
                </Route>

                {/* ────────── DİL PREFIX'LI (EN/DE/RU/AR) ────────── */}
                <Route path="/:lang" element={<LangPrefixGuard><GuardOutlet /></LangPrefixGuard>}>
                    <Route index element={<Home />} />
                    <Route path="suits-and-villas" element={<SuitsAndVillas />} />
                    <Route path="suite-villas" element={<Suits />} />
                    <Route path="garden-villas" element={<Villas />} />
                    <Route path="contact" element={<Contact />} />
                    <Route path="about" element={<Navigate to="life-at-hiranova" replace />} />
                    <Route path="facility" element={<Facility />} />
                    <Route path="life-at-hiranova" element={<LifeAtHiraNova />} />
                    <Route path="restaurant" element={<Restaurant />} />
                    <Route path="location" element={<Location />} />
                    <Route path="gallery" element={<Gallery />} />
                    <Route path="genel-plan" element={<GenelPlan />} />
                    <Route path="villa/:villaId" element={<VillaDetail />} />
                    <Route path="tesis/:amenityId" element={<AmenityDetail />} />
                    <Route path="felsefe" element={<Navigate to="life-at-hiranova" replace />} />
                    <Route path="destinasyonlar" element={<Destinations />} />
                    <Route path="uyelik-basvurusu" element={<Membership />} />
                    <Route path="sosyal-kulup" element={<SosyalKulup />} />
                    <Route path="aktiviteler" element={<Activities />} />
                    <Route path="cocuk-kulubu" element={<Academy />} />
                    <Route path="cocuk-akademisi" element={<Academy />} />
                    <Route path="temsilcilerimiz" element={<Representatives />} />
                    <Route path="uyelik-paketleri" element={<Packages />} />
                </Route>
            </PageTransition>
            </Suspense>
            </ErrorBoundary>
            {/* Tüm sayfalarda görünür mobile CTA — detay sayfalarında kendini gizler */}
            <GlobalMobileBar />
            {/* Sağ kenar: ney aç/kapa + tanıtım filmi modalı */}
            <SiteAudioBar />
            <ChatWidget />
        </Router>
    )
}

// react-router-dom: parent route element içinde <Outlet /> render eder.
function GuardOutlet() {
  return <Outlet />
}

export default App
